export { ExtendedObject as Object } from './object';
export type { NonExtensible, Sealed, Frozen } from './object';

// Convenient aliases.
export type nil = null | undefined;
export type procedure = () => void;

/**
 * Represents a percentage, [0,1].
 */
export type percentage = number;

/**
 * Helper alias for any, should only be used to simplify migrations.
 * It tracks places where we don't want to make structural changes
 * during a migration to fix the issue, but leaves them findable for
 * future cleanup.
 */
export type AnyDuringMigration = any;

export type AppendOnlyArray<T> = ReadonlyArray<T> & Pick<Array<T>, 'push'>;

// A non-empty array type. Note that this is not sound if you call pop() or
// shift() more than once; that can be resolved if this TypeScript feature gets
// implemented:
//  https://github.com/microsoft/TypeScript/issues/31376
export type NonEmptyArray<T> = {
  concat(...items: ConcatArray<T>[]): NonEmptyArray<T>;
  concat(...items: (T | ConcatArray<T>)[]): NonEmptyArray<T>;
  map<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): NonEmptyArray<U>;
  pop(): T;
  reverse(): NonEmptyArray<T>;
  shift(): T;
  sort(compareFn?: (a: T, b: T) => number): NonEmptyArray<T>;
} & Array<T>;

export function isNonEmpty<T>(array: Array<T>): array is NonEmptyArray<T> {
  return array.length > 0;
}

export function isNonNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

// An object for which each property optionally contains a T. This is a
// shorthand that allows you to avoid specifying index signatures for lookups in
// simple object literals when compiling with 'noImplicitAny'.
export type RecordOf<T> = Record<string, T | undefined>;

export type assertExtends<A extends B, B> = never;

// Situation-specific replacement for `assertExtends()`, for clarity.
export type assertConstructorImplements<
  Constructor extends B,
  B extends { new (...args: any[]): any },
> = never;

// Fail to compile if A and B are unequal.
export type assertTypesEqual<A extends B, B extends _ref, _ref extends any = A> = A;

/**
 * Select is a recursive version of Pick. Select<I, M> = P, where P
 * is a partial copy of I determined by M, a Mask. Used in recording
 * code as part of a dependency injection pattern.
 */
export type Select<Interface extends Object, SubInterfaceMask extends Mask<Interface>> = {
  [K in keyof Interface & keyof SubInterfaceMask]: SubInterfaceMask[K] extends true
    ? Interface[K]
    : Interface[K] extends Object
    ? Select<Interface[K], SubInterfaceMask[K]>
    : never;
};

type Mask<I> = {
  [K in keyof I]?: true | Mask<I[K]>;
};

/**
 * In basic usage, Select<Interface, Mask> uses a mask that maps a subset of
 * properties in the input type Interface to the type 'true'. The 'true'
 * properties in the mask will be included in the output type; all other
 * properties will be removed. This is equivalent to Pick, except that the mask
 * is expressed differently.
 */
export type _Select_example_1 = assertTypesEqual<
  { a: string; c: void },
  Select<
    { a: string; b: number; c: void }, // The input interface.
    { a: true; c: true } // A mask that selects 'a' and 'c'.
  >
>;

/**
 * Select<Interface, Mask> can also be used recursively by specifying an object
 * type for a mask property instead of the type 'true'. The object type is just
 * another nested mask for the value type of that property. Masks can be nested
 * recursively to an arbitrary depth.
 */
export type _Select_example_2 = assertTypesEqual<
  { a: { b: { '2': number } }; x: {} },
  Select<
    // The input interface.
    {
      a: { b: { '1': string; '2': number; '3': any } };
      x: { y: { z: void } };
      q: string;
    },
    // A mask that selects the property Interface#a#b#2 and the property
    // Interface#x. Note that we selected Interface#x, but not any of its
    // properties! If we had used 'x: true' instead, we would have gotten all of
    // the properties of Interface#x.
    { a: { b: { '2': true } }; x: {} }
  >
>;

// Assigned<T, U>, where T and U are object types, yields a type containing all
// properties from T which are not in U and all properties from U. Think
// `Object.assign()`, but at the type level. Note that this is not an
// intersection; if the property 'foo' exists in both T and U, the type of T's
// 'foo' makes no contribution to the type of 'foo' in Assigned<T, U>.
export type Assigned<T extends object, U extends object> = Omit<T, keyof U> & U;

// PartialExcept<T, RequiredKeys>, where T is an object type and RequiredKeys is
// a union of properties names from T, yields a type which has the same
// properties as T, except that all properties *not* in Required are optional.
// In other words, PartialExcept<T, RequiredKeys> is like Partial<T> except that
// it doesn't change the properties in RequiredKeys. If some of the properties
// in RequiredKeys are already optional, they remain optional; PartialExcept<T,
// RequiredKeys> always yields a type which is strictly more optional than T.
export type PartialExcept<T extends object, RequiredKeys extends keyof T> = Assigned<
  Partial<T>,
  Pick<T, RequiredKeys>
>;

// PartialForKeys<T, PartialKeys> is the inverse of
// PartialExcept<T, RequiredKeys> - it yields a type which has the same
// properties as T, except that the properties in PartialKeys are optional.
export type PartialForKeys<T extends object, PartialKeys extends keyof T> = Assigned<
  T,
  Partial<Pick<T, PartialKeys>>
>;

// Mutable<T> yields a type with the same properties as T, but with
// all modifiers removed, so that `readonly` properties become mutable. Note
// that other modifiers, and in particular the optional modifier (`?`), are
// also removed, so the resulting type may be stricter in some ways than the
// original.
//
// Copied from https://github.com/microsoft/TypeScript/issues/24509.
export type Mutable<T extends object> = {
  -readonly [P in keyof T]: T[P];
};

/**
 * Fail to compile if the type of the value passed to this function is not
 * `never` - bottom, basically. If a failure occurs at runtime, an exception is
 * thrown. (Use claimExhaustive if you want to print a warning and continue
 * instead.) As the name implies, use this for exhaustiveness checking when
 * working with discriminated unions or unions of literals.
 *
 * Example:
 *  const value: {tag: 'a'} | {tag: 'b'} | {tag: 'c'} = getValue();
 *  switch (value.tag) {
 *    case 'a': case 'b': case 'c': return;
 *    default: assertExhaustive(value);  // Compiles.
 *  }
 *  switch (value.tag) {
 *    case 'a': case 'b': return;
 *    default: assertExhaustive(value);  // Missed a case; won't compile.
 *  }
 *  switch (value as 'a' | 'b') {
 *    case 'a': case 'b': return;
 *    default: assertExhaustive(value);  // Works for literal unions too.
 *  }
 *
 * @see claimExhaustive
 */
export function assertExhaustive(
  _value: never,
  message: string = 'Reached unexpected case in exhaustive switch',
): never {
  throw new Error(message);
}

/**
 * Fail to compile if the type of the value passed to this function is not
 * `never` - bottom, basically. If a failure occurs at runtime, a warning is
 * printed to the console. (Use assertExhaustive if you want to throw an
 * exception instead.) As the name implies, use this for exhaustiveness checking
 * when working with discriminated unions or unions of literals.
 *
 * @see assertExhaustive
 */
export function claimExhaustive(
  _value: never,
  message: string = 'Reached unexpected case in exhaustive switch',
): void {
  type MaybeHasConsole = { console?: { warn?: (message: string) => void } };
  const maybeHasConsole = globalThis as MaybeHasConsole | undefined;
  if (typeof maybeHasConsole?.console?.warn === 'function') {
    maybeHasConsole.console.warn(message);
  }
}

// A type which has the same properties as T, but allows any of them to be null.
// Compare to the built-in Partial<T>.
export type Nullable<T> = { [P in keyof T]: T[P] | null };

// Given a discriminated union type:
//   type FooA = {tag: 'a', str: string};
//   type FooB = {tag: 'b', num: number};
//   type Foo = FooA | FooB;
// `SelectFromUnion<Foo, {tag: 'a'}>` evaluates to `FooA`.
//
// In other words, SelectFromUnion evaluates to one of the union member types,
// given a value for the discriminating tag. The implementation uses
// distributive conditional types to remove all members of the union which don't
// match the tag.
export type SelectFromUnion<Union, Tag> = Union extends Tag ? Union : never;

// AssociatedType<T> is used to signal to human readers that a property exists
// only to make its type accessible. For example:
//   interface Pointer {
//     _DeviceType: AssociatedType<unknown>;
//     _ButtonStateType: AssociateType<boolean[]>;
//     doClick(device: this['_DeviceType'], button: number): this['_ButtonStateType'];
//   }
//   class MousePointer implements Pointer {
//     _DeviceType!: AssociatedType<Mouse>;
//     _ButtonStateType!: AssociatedType<MouseButtonState[]>;
//     doClick(device: Mouse, button: number): MouseButtonState[];
//   }
//   function click<P extends Pointer>(
//     pointer: P,
//     device: P['_DeviceType']
//   ): P['_ButtonStateType'] {
//     return pointer.doClick(device, 0);
//   }
//
// In this example, all implementations of Pointer have '_DeviceType' and
// '_ButtonStateType' properties, and the generic function click() can use the
// type of these properties when it needs to refer to the type of device or
// button state associated with a specific Pointer implementation. In many
// programming languages this is called an "associated type". (Though in other
// languages you'd be able to just write `type DeviceType: Mouse;` instead of
// defining a property.)
//
// There are other ways of tying together closely related types in TypeScript,
// but they have disadvantages compared to associated types. The main
// alternative is conditional types. You can construct a mapping between types
// as follows:
//   type DeviceType<P extends Pointer> = P extends MousePointer ? Mouse
//                                      : P extends StylusPointer ? Digitizer
//                                      : never;

// This isn't very extensible because it defines a "closed type family": you
// cannot add new Pointer types without changing the definition of DeviceType.
// In contrast, associated types define an "open type family", which can be
// extended without updating any central location. Beyond extensibility, the
// closed nature of conditional types means that all types in the family must be
// visible in the same module, which can cause problems with circular references
// which are quite tricky to resolve.
//
// Using conditional types in this way also requires additional type parameters
// in generic code:
//   click<
//     P extends Pointer,
//     D extends DeviceType<P>,
//     B extends ButtonStateType<P>
//   >(p: P, d: D): B { }
//
// This can get onerous quickly. It's especially bad if you have a type
// parameter that can't be inferred; because TypeScript infers either all type
// paramters or none, you'd need to specify all of these parameters manually.
//
// The main *disadvantage* of using associated types is that TypeScript doesn't
// have first-class support for them, so you're forced to define a property.
// This can confuse human readers, which is why AssociatedType<T> exists.
export type AssociatedType<T> = T;

export type Primitive = undefined | null | boolean | string | number | Function;

// DeepPartial<T> is Partial<T> applied recursively.
// The type is unioned with T to hint to the compiler that T
//   is assignable to DeepPartial<T>.
export type DeepPartial<T> =
  | T
  | (T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : never);

export type DeepRequired<T> = T extends Primitive ? T : { [K in keyof T]-?: DeepRequired<T[K]> };

export type TupleElement<L extends readonly any[]> = L[number];

// OmitStrict is like the built-in Omit type, except that it strictly requires
// that a key supplied in the type parameter is a key of T.
export type OmitStrict<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export function pick<T extends {}, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
  const ret = {} as Pick<T, K>;
  keys.forEach(k => {
    ret[k] = obj[k];
  });
  return ret;
}

export function omit<T extends {}, K extends keyof T>(obj: T, ...keysToOmit: K[]): Omit<T, K> {
  const ret: T = { ...obj };
  Object.keys(ret).forEach(prop => {
    const k = prop as K;
    if (keysToOmit.indexOf(k) !== -1) {
      delete ret[k];
    }
  });
  return ret as Omit<T, K>;
}

export type ElementOf<T extends readonly any[]> = T extends readonly (infer U)[] ? U : never;

/**
 * Explicitly note that the given promise does not need to be awaited.
 *
 * `tsetse` (https://tsetse.info), used by `bazel`'s TS rules, adds some extra
 * conformance checks on top of eslint/tslint. One of which enforces the usage
 * of promises from within async functions, trying to ensure they are awaited
 * (https://tsetse.info/must-use-promises). This method bypasses that check by
 * forcing the developer to leave a lint-safe marker that the promise is fine to
 * ignore.
 */
export function notAwaited(promise: Promise<unknown>): void {
  // Does nothing.
}

export type NonNullableObject<T> = { [K in keyof T]: NonNullable<T[K]> };

export type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
  [Property in Key]-?: Type[Property];
};

// Types for https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
declare global {
  interface Document {
    hasStorageAccess?: () => Promise<boolean>;
    requestStorageAccess?: () => Promise<void>;
  }
}

/**
 * Helper to make the IDE hover overlay more readable. Combines intersections,
 * expands nested types, etc.
 *
 * eg:  const ugly: App & Omit<ConnectedApp, 'app'> = {}
 *             ^?: App & Omit<ConnectedApp, "app">
 *
 *      const pretty = Prettify<App & Omit<ConnectedApp, 'app'> = {}
 *              ^?: { id: string, name: string, owner: string, ... }
 */
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

/**
 * Represents a value that must be JSON serializable.
 */
export type JsonSerializable =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JsonSerializable }
  | JsonSerializable[];
